/**
 * Filterable tutorial title directory used by the tutorial blog template.
 * The element is intentionally self-contained so it reconnects automatically
 * when blog-tags.js replaces #Blog-Content after an AJAX tag change.
 */
if (!customElements.get("blog-directory")) {
  customElements.define(
    "blog-directory",
    class BlogDirectory extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          this.tooltipTriggers = Array.from(
            this.querySelectorAll("[data-tutorial-directory-tooltip]"),
          );
          this.initTooltips();
          return;
        }

        this.input = this.querySelector("[data-tutorial-directory-input]");
        this.clearButton = this.querySelector(
          "[data-tutorial-directory-clear]",
        );
        this.count = this.querySelector("[data-tutorial-directory-count]");
        this.empty = this.querySelector("[data-tutorial-directory-empty]");
        this.details = this.querySelector(".tutorial-directory__details");
        this.groups = Array.from(
          this.querySelectorAll("[data-tutorial-directory-group]"),
        );
        this.items = Array.from(
          this.querySelectorAll("[data-tutorial-directory-item]"),
        );
        this.tooltipTriggers = Array.from(
          this.querySelectorAll("[data-tutorial-directory-tooltip]"),
        );
        this.total = this.items.length;

        this.initTooltips();

        if (!this.input) return;
        this.initialized = true;

        this.mobileQuery = window.matchMedia("(max-width: 989px)");
        this.syncDisclosure(this.mobileQuery);
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener("change", (event) =>
            this.syncDisclosure(event),
          );
        } else {
          this.mobileQuery.addListener((event) => this.syncDisclosure(event));
        }

        if (!this.items.length) {
          this.input.disabled = true;
          if (this.empty) {
            this.empty.textContent =
              "No directory items have been added yet.";
            this.empty.classList.remove("hidden");
          }
          return;
        }

        this.items.forEach((item) => {
          const title =
            item.dataset.title ||
            item.querySelector("a")?.textContent ||
            item.textContent ||
            "";
          item._normalizedTitle = this.normalize(title);
        });

        this.groups.forEach((group) => {
          group._directoryItems = Array.from(
            group.querySelectorAll("[data-tutorial-directory-item]"),
          );
        });

        this.input.addEventListener("input", () => this.filter());
        this.input.addEventListener("search", () => this.filter());
        this.input.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && this.input.value) {
            event.preventDefault();
            this.clear();
          }
        });

        if (this.clearButton) {
          this.clearButton.addEventListener("click", () => this.clear());
        }
      }

      disconnectedCallback() {
        this.destroyTooltips();
      }

      initTooltips() {
        if (this.tooltip || !this.tooltipTriggers.length) return;

        this.tooltip = document.createElement("div");
        this.tooltip.className = "tutorial-directory__tooltip";
        this.tooltip.setAttribute("role", "tooltip");
        this.tooltip.hidden = true;
        document.body.appendChild(this.tooltip);

        this.tooltipListeners = [];
        this.tooltipTriggers.forEach((trigger) => {
          const onPointerEnter = (event) => {
            if (event.pointerType !== "touch") {
              this.showTooltip(trigger, "pointer");
            }
          };
          const onPointerLeave = (event) => {
            if (event.pointerType !== "touch") this.hideTooltip();
          };
          const onPointerDown = (event) => {
            if (event.pointerType === "touch") {
              this.showTooltip(trigger, "touch");
              window.clearTimeout(this.tooltipTimeout);
              this.tooltipTimeout = window.setTimeout(
                () => this.hideTooltip(),
                4000,
              );
            }
          };
          const onFocus = () => {
            if (trigger.matches(":focus-visible")) {
              this.showTooltip(trigger, "keyboard");
            }
          };
          const onBlur = () => this.hideTooltip();
          const onKeyDown = (event) => {
            if (event.key === "Escape") this.hideTooltip();
          };

          trigger.addEventListener("pointerenter", onPointerEnter);
          trigger.addEventListener("pointerleave", onPointerLeave);
          trigger.addEventListener("pointerdown", onPointerDown);
          trigger.addEventListener("focus", onFocus);
          trigger.addEventListener("blur", onBlur);
          trigger.addEventListener("keydown", onKeyDown);
          this.tooltipListeners.push({
            trigger,
            onPointerEnter,
            onPointerLeave,
            onPointerDown,
            onFocus,
            onBlur,
            onKeyDown,
          });
        });

        this.onTooltipViewportChange = () => {
          if (!this.activeTooltipTrigger) return;
          if (this.tooltipInputType === "touch") {
            this.hideTooltip();
          } else {
            this.positionTooltip(this.activeTooltipTrigger);
          }
        };
        this.onTooltipDocumentPointerDown = (event) => {
          if (
            this.tooltipInputType === "touch" &&
            !event.target.closest("[data-tutorial-directory-tooltip]")
          ) {
            this.hideTooltip();
          }
        };
        window.addEventListener("resize", this.onTooltipViewportChange);
        document.addEventListener("scroll", this.onTooltipViewportChange, true);
        document.addEventListener(
          "pointerdown",
          this.onTooltipDocumentPointerDown,
          true,
        );
      }

      showTooltip(trigger, inputType) {
        if (!this.tooltip || !trigger.dataset.tutorialDirectoryTooltip) return;

        window.clearTimeout(this.tooltipTimeout);
        this.activeTooltipTrigger = trigger;
        this.tooltipInputType = inputType;
        this.tooltip.textContent = trigger.dataset.tutorialDirectoryTooltip;
        this.tooltip.hidden = false;
        this.positionTooltip(trigger);
      }

      positionTooltip(trigger) {
        if (!this.tooltip || this.tooltip.hidden || !trigger.isConnected) return;

        const gap = 8;
        const viewportPadding = 12;
        const triggerRect = trigger.getBoundingClientRect();
        const tooltipRect = this.tooltip.getBoundingClientRect();
        let left = triggerRect.right + gap;
        let top =
          triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;

        if (left + tooltipRect.width > window.innerWidth - viewportPadding) {
          left = triggerRect.left - tooltipRect.width - gap;
        }
        if (left < viewportPadding) {
          left = Math.min(
            Math.max(triggerRect.left, viewportPadding),
            window.innerWidth - tooltipRect.width - viewportPadding,
          );
          top = triggerRect.bottom + gap;
          if (top + tooltipRect.height > window.innerHeight - viewportPadding) {
            top = triggerRect.top - tooltipRect.height - gap;
          }
        }

        left = Math.max(
          viewportPadding,
          Math.min(
            left,
            window.innerWidth - tooltipRect.width - viewportPadding,
          ),
        );
        top = Math.max(
          viewportPadding,
          Math.min(
            top,
            window.innerHeight - tooltipRect.height - viewportPadding,
          ),
        );
        this.tooltip.style.left = `${Math.round(left)}px`;
        this.tooltip.style.top = `${Math.round(top)}px`;
      }

      hideTooltip() {
        window.clearTimeout(this.tooltipTimeout);
        if (this.tooltip) this.tooltip.hidden = true;
        this.activeTooltipTrigger = null;
        this.tooltipInputType = null;
      }

      destroyTooltips() {
        window.clearTimeout(this.tooltipTimeout);
        (this.tooltipListeners || []).forEach((listener) => {
          listener.trigger.removeEventListener(
            "pointerenter",
            listener.onPointerEnter,
          );
          listener.trigger.removeEventListener(
            "pointerleave",
            listener.onPointerLeave,
          );
          listener.trigger.removeEventListener(
            "pointerdown",
            listener.onPointerDown,
          );
          listener.trigger.removeEventListener("focus", listener.onFocus);
          listener.trigger.removeEventListener("blur", listener.onBlur);
          listener.trigger.removeEventListener("keydown", listener.onKeyDown);
        });
        window.removeEventListener("resize", this.onTooltipViewportChange);
        document.removeEventListener(
          "scroll",
          this.onTooltipViewportChange,
          true,
        );
        document.removeEventListener(
          "pointerdown",
          this.onTooltipDocumentPointerDown,
          true,
        );
        this.tooltip?.remove();
        this.tooltip = null;
      }

      normalize(value) {
        return String(value)
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLocaleLowerCase()
          .trim();
      }

      syncDisclosure(viewport) {
        if (!this.details) return;

        const isMobile = viewport.matches;
        if (isMobile && this.lastViewportWasMobile !== true) {
          this.details.open = false;
        } else if (!isMobile) {
          this.details.open = true;
        }
        this.lastViewportWasMobile = isMobile;
      }

      filter() {
        const term = this.normalize(this.input.value);
        const tokens = term.split(/\s+/).filter(Boolean);
        let visible = 0;

        this.items.forEach((item) => {
          const matches = tokens.every((token) =>
            item._normalizedTitle.includes(token),
          );
          item.hidden = !matches;
          if (matches) visible += 1;
        });

        this.groups.forEach((group) => {
          const groupVisible = group._directoryItems.filter(
            (item) => !item.hidden,
          ).length;
          const groupCount = group.querySelector(
            "[data-tutorial-directory-group-count]",
          );

          if (groupCount) {
            groupCount.textContent = String(
              tokens.length
                ? groupVisible
                : groupCount.dataset.total || group._directoryItems.length,
            );
          }

          if (tokens.length) {
            if (group._openBeforeSearch === undefined) {
              group._openBeforeSearch = group.open;
            }
            group.hidden = groupVisible === 0;
            group.open = groupVisible > 0;
          } else {
            group.hidden = false;
            if (typeof group._openBeforeSearch === "boolean") {
              group.open = group._openBeforeSearch;
              group._openBeforeSearch = undefined;
            }
          }
        });

        if (this.count) this.count.textContent = String(visible);
        if (this.empty) this.empty.classList.toggle("hidden", visible !== 0);
        if (this.clearButton) {
          this.clearButton.classList.toggle("hidden", term.length === 0);
        }
      }

      clear() {
        this.input.value = "";
        this.filter();
        this.input.focus();
      }
    },
  );
}
